/**
 * Onde o corpo de cada escritório ficou — a memória de posição, fora do React.
 *
 * Uma sessão de escritório pode ser aberta e fechada dez vezes (carregar em
 * «Discord» desmonta o `OfficeSpace` inteiro) e quem volta tem de voltar ao
 * sítio onde estava, não à Recepção — que dava a sensação de ter sido despejado
 * de uma sala de onde nunca se saiu. Por isso a posição vive num `Map` ao nível
 * do módulo: uma `useRef` nasce a `null` a cada remontagem, e era isso que
 * mandava toda a gente para a entrada.
 *
 * É de SESSÃO. Um F5 continua a nascer na Recepção, e é o que o servidor também
 * faz — a posição vive na sessão do socket, não na base de dados.
 *
 * Em desenvolvimento vive dentro do `import.meta.hot.data`, que o Vite passa da
 * instância antiga do módulo para a nova. Sem isso, guardar QUALQUER ficheiro do
 * escritório reexecutava o módulo, o mapa renascia vazio e toda a gente
 * reaparecia na Recepção a meio do trabalho — metade do defeito que parecia "o
 * escritório voltou às origens". O Fast Refresh não é um F5.
 *
 * Vivia dentro do `OfficeSpace` e saiu para aqui quando deixou de ser só dele:
 * com a call a segurar o avatar no mapa depois de se carregar em «Discord», é o
 * `useOfficePresence` que precisa de saber onde o corpo ficou para o repor tal e
 * qual quando a ligação volta. Importá-lo do componente fechava um ciclo — o
 * `OfficeSpace` já importa a regra de presença do hook — e um módulo de dados no
 * meio corta-o.
 */

import type { Facing, Snowflake } from "@greenvoice/shared";

/** Um corpo pousado no mapa: onde está e para onde olha. */
export type Corpo = { readonly x: number; readonly y: number; readonly facing: Facing };

const ultimaPosicao: Map<Snowflake, Corpo> = import.meta.hot?.data?.ultimaPosicao ?? new Map();
if (import.meta.hot?.data) import.meta.hot.data.ultimaPosicao = ultimaPosicao;

/** Guarda onde este corpo ficou. Cópia à entrada: o loop do Phaser escreve na
 *  sua posição a 60fps e não pode mutar a memória por baixo de quem a lê. */
export function lembrarCorpo(spaceId: Snowflake, corpo: Corpo): void {
  ultimaPosicao.set(spaceId, { x: corpo.x, y: corpo.y, facing: corpo.facing });
}

/** Onde este corpo ficou — ou `null` se ainda não esteve neste escritório nesta
 *  sessão, que é sempre o caso de quem só usa o telemóvel. */
export function corpoLembrado(spaceId: Snowflake): Corpo | null {
  return ultimaPosicao.get(spaceId) ?? null;
}
